<template>
  <div v-if="user" class="edit-form py-3">
    <v-layout column align-center justify-center>
      <v-card
        elevation="4"
        class="mx-auto mt-12 mb-5 px-10"
        min-width="320"
        max-width="600"
        ref="form"
      >
        <v-card-title>
          {{ user_info.name || "New User" }}
          <v-row
            ><v-layout justify-end>
              <v-btn
                v-if="read_only && is_admin & !is_self & !disable_edit"
                x-small
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                :disabled="busy"
                @click="resend_verification(user)"
              >
                Resend Verification
              </v-btn>
            </v-layout></v-row
          >
        </v-card-title>
        <v-card-subtitle v-if="!user_creation">
          <v-row class="text-left" no-gutters>
            <v-col class="mx-auto">
              <v-row>
                <v-col cols="auto" id="user-email-subtitle">
                  {{ user_info.email }}
                  <span v-if="user_info.active">&nbsp;(Active)</span>
                  <span v-else>&nbsp;(Inactive)</span>
                </v-col>
                <v-col cols="auto">
                  <user-actions
                    :user="user_info"
                    show_usage
                    show_orders
                  ></user-actions>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="text-left" no-gutters>
            <v-col>
              <v-row>
                <v-col cols="auto">
                  {{ account_info.name }}
                </v-col>
                <v-col cols="auto">
                  <account-actions
                    :account="account_info"
                    show_users
                    show_usage
                    show_orders
                    show_details
                  ></account-actions>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text>
          <hr />
          <br />

          <v-row
            ><v-layout justify-center>
              <v-btn
                id="enable-user-button"
                v-if="read_only && !user_info.active & is_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="enable_user()"
                :disabled="busy"
              >
                Enable User
              </v-btn>
              <v-btn
                id="disable-user-button"
                v-if="
                  read_only &&
                  !disable_edit &&
                  user_info.active & is_admin & !is_self
                "
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="disable_user()"
                :disabled="busy"
              >
                Disable User
              </v-btn>
              <v-btn
                id="edit-user-button"
                v-if="read_only && !disable_edit & is_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                :disabled="busy"
                @click="toggle_readonly()"
              >
                Edit User
              </v-btn>
            </v-layout></v-row
          >

          <!-- Read Only View -->
          <v-row v-if="read_only">
            <!-- Account info -->
            <v-col>
              <v-row>
                <v-col>
                  Info

                  <div
                    id="user-info-container"
                    v-bind:style="{ fontSize: '14px' }"
                    class="mx-4"
                  >
                    Name: {{ user_info.name }} <br />
                    User ID: {{ user_info.user_id }} <br />
                    Job Title: {{ user_info.job_title }} <br />
                    Email: {{ user_info.email }} <br />
                    Country: {{ get_country_name }} <br />
                    Account: {{ account_info.name }} <br />
                    Account Administrator: {{ user_info.admin }} <br />
                  </div>

                  Fee Limits

                  <div v-bind:style="{ fontSize: '14px' }" class="mx-4">
                    Annual Archive Subscription Fee Limit:
                    {{ user_info.limits.annual_subscription_fee_limit }} <br />
                    Fresh Archive Imagery Fee Limit:
                    {{ user_info.limits.fresh_imagery_fee_limit }} <br />
                    Standard Archive Imagery Fee Limit:
                    {{ user_info.limits.standard_imagery_fee_limit }} <br />
                    Training Archive Imagery Fee Limit:
                    {{ user_info.limits.training_imagery_fee_limit }} <br />
                    Tasking Imagery Fee Limit:
                    {{ user_info.limits.tasking_imagery_fee_limit }} <br />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Edit View -->
          <v-row v-if="!read_only">
            <v-col>
              Info
              <br /><br />

              <v-row v-if="!user_creation">
                <v-col>
                  <v-text-field
                    label="Account"
                    v-model="account_info.name"
                    :disabled="!user_creation"
                    hint="Required"
                    persistent-hint
                    outlined
                    dense
                    :readonly="read_only"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="accounts.length > 1 && user_creation">
                <v-col>
                  <v-autocomplete
                    ref="account"
                    v-model="user_info.account_id"
                    :items="accounts"
                    item-text="name"
                    item-value="account_id"
                    :disabled="!user_creation"
                    outlined
                    dense
                    clearable
                    open-on-clear
                    label="Account"
                    hint="Required. The account the user will be created in."
                    persistent-hint
                    :rules="[(v) => !!v || 'Account is required']"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-name"
                    ref="name"
                    label="Name"
                    v-model="user_info.name"
                    outlined
                    dense
                    hint="Required"
                    persistent-hint
                    :rules="[(v) => !!v || 'Name is required']"
                    :readonly="read_only"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-email"
                    ref="email"
                    label="Email"
                    v-model="user_info.email"
                    :disabled="!user_creation"
                    outlined
                    dense
                    hint="Required"
                    persistent-hint
                    :rules="[
                      (v) => !!v || 'Email is required',
                      (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                    ]"
                    :readonly="read_only"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    v-model="user_info.job_title"
                    label="Job Title"
                    outlined
                    dense
                    :readonly="read_only"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete
                    id="user-profile-country"
                    ref="country"
                    v-model="user_info.country_code"
                    :items="countries"
                    :item-text="(item) => `${item.abbr} - ${item.name}`"
                    item-value="abbr"
                    outlined
                    dense
                    clearable
                    open-on-clear
                    label="Country"
                    hint="Required. The country where the user resides."
                    persistent-hint
                    :rules="[(v) => !!v || 'Country is required']"
                    :disabled="read_only"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col>
                  <v-switch
                    v-model="user_info.admin"
                    label="Account Administrator"
                    :readonly="read_only"
                    class="pl-4"
                  ></v-switch>
                </v-col>
              </v-row>

              Fee Limits
              <br />
              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-annual-imagery"
                    :value="user_info.limits.annual_subscription_fee_limit"
                    @input="
                      user_info.limits.annual_subscription_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    hint="Limit in dollars (-1 to use account limit)"
                    type="number"
                    class="pt-5"
                    label="Annual Archive Subscription Fee Limit"
                    outlined
                    dense
                    :readonly="read_only"
                  >
                    <!--<template v-slot:prepend="">Annual Subscription Fee</template>-->
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-fresh-imagery"
                    :value="user_info.limits.fresh_imagery_fee_limit"
                    @input="
                      user_info.limits.fresh_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    hint="Limit in dollars (-1 to use account limit)"
                    type="number"
                    label="Fresh Archive Imagery Fee Limit"
                    outlined
                    dense
                    :readonly="read_only"
                  >
                    <!--<template v-slot:prepend="">Annual Subscription Fee</template>-->
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-standard-imagery"
                    :value="user_info.limits.standard_imagery_fee_limit"
                    @input="
                      user_info.limits.standard_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    hint="Limit in dollars (-1 to use account limit)"
                    type="number"
                    label="Standard Archive Imagery Fee Limit"
                    outlined
                    dense
                    :readonly="read_only"
                  >
                    <!--<template v-slot:prepend="">Annual Subscription Fee</template>-->
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-training-imagery"
                    :value="user_info.limits.training_imagery_fee_limit"
                    @input="
                      user_info.limits.training_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    hint="Limit in dollars (-1 to use account limit)"
                    type="number"
                    label="Training Archive Imagery Fee Limit"
                    outlined
                    dense
                    :readonly="read_only"
                  >
                    <!--<template v-slot:prepend="">Annual Subscription Fee</template>-->
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-text-field
                    id="user-profile-tasking-imagery"
                    :value="user_info.limits.tasking_imagery_fee_limit"
                    @input="
                      user_info.limits.tasking_imagery_fee_limit =
                        $event !== '' ? Number($event) : null
                    "
                    hint="Limit in dollars (-1 to use account limit)"
                    type="number"
                    label="Tasking Imagery"
                    outlined
                    dense
                    :readonly="read_only"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row v-if="!read_only"
            ><v-layout justify-center>
              <v-btn
                id="edit-user-cancel-button"
                v-if="!user_creation && is_admin"
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                :disabled="busy"
                @click="toggle_readonly()"
              >
                Cancel
              </v-btn>
              <v-btn
                id="create-user-cancel-button"
                v-if="user_creation && is_admin"
                class="ma-4"
                elevation="4"
                outlined
                color="#E5E1DA"
                @click="
                  $router.push({
                    name: 'user-landing',
                  })
                "
              >
                Cancel
              </v-btn>
              <v-btn
                id="create-user-save-button"
                v-if="user_creation & is_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                @click="create_user(user)"
                :disabled="busy"
              >
                Create User
              </v-btn>
              <v-btn
                id="edit-user-save-button"
                v-if="!user_creation & is_admin"
                class="ma-4 button_text--text"
                elevation="4"
                color="primary"
                :disabled="busy"
                @click="update_user(user)"
              >
                Save User
              </v-btn>
            </v-layout></v-row
          >
        </v-card-text>
      </v-card>

      <div class="float-right caption" v-if="user_info.created">
        <span
          >User created:
          {{ new Date(user_info.created).toLocaleString() }}</span
        >
        &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
        <span
          >Last modified:
          {{ new Date(user_info.modified).toLocaleString() }}</span
        >
      </div>
    </v-layout>
  </div>
</template>

<script>
import countries from "@/app/countries";
import UserActions from "@/components/user/UserActions.vue";
import AccountActions from "@/components/account/AccountActions.vue";
import { mapGetters } from "vuex";
import utils from "@/utils/userType";

export default {
  components: {
    UserActions,
    AccountActions,
  },
  props: {
    user: Object,
    account: Object,
    accounts: Array,
    read_only: Boolean,
    user_creation: Boolean,
    busy: Boolean,
  },
  data() {
    return {
      min: -1,
      max: 1000,
      annualSlider: 50,
      freshSlider: 50,
      standardSlider: 50,
      trainingSlider: 50,
      activeSwitch: true,
      countries: countries.countries,
      requiredFields: ["name", "email", "country", "account"],
    };
  },
  computed: {
    ...mapGetters(["user_details"]),
    user_info() {
      if (!this.user) {
        return {};
      }
      return this.user;
    },
    account_info() {
      if (!this.account) {
        return {};
      }
      return this.account;
    },
    is_admin() {
      return utils.is_admin(this.user_details);
    },
    disable_edit() {
      if (this.user_details.super_admin) {
        return false;
      } else if (
        this.user_details.admin &&
        this.user.account_id == this.user_details.account_id
      ) {
        return false;
      } else {
        return true;
      }
    },
    is_self() {
      if (this.user_details.user_id === this.$route.params.user_id) {
        return true;
      } else {
        return false;
      }
    },
    get_country_name() {
      if (this.user_info.country_code) {
        let country = countries.countries.filter(
          (item) => item.abbr === this.user_info.country_code
        );
        return `${this.user_info.country_code} - ${country[0].name}`;
      } else {
        return null;
      }
    },
  },
  methods: {
    toggle_readonly() {
      // TODO: see if logged in user is admin
      this.$emit("read_only_changed");
    },
    update_user(user) {
      this.$emit("update_user", { user: user });
    },
    create_user(user) {
      // remove account requirement if there is 1 account
      if (this.accounts <= 1) {
        this.requiredFields.splice(this.requiredFields.indexOf("account"), 1);
      }

      this.requiredFields.forEach((f) => {
        this.$refs[f].validate(true);
      });

      if (user.name && user.email && user.country_code) {
        this.$emit("create_user", { user: user });
      }
    },
    enable_user() {
      this.$emit("enable_user");
    },
    disable_user(user) {
      this.$emit("disable_user", { user: user });
    },
    resend_verification(user) {
      this.$emit("resend_verification", { user: user });
    },
  },
};
</script>
<style lang="scss">
.v-application .error--text {
  color: #df323b !important;
}
</style>
